import {
  type CreateKnowledgeArticle,
  type CreateKnowledgeFolder,
  type GlossaryProposal,
  GlossaryProposalListSchema,
  GlossaryProposalSchema,
  type GlossaryProposalStatus,
  type KnowledgeArticleDetail,
  KnowledgeArticleDetailSchema,
  type KnowledgeFolder,
  KnowledgeFolderSchema,
  type KnowledgeTree,
  KnowledgeTreeSchema,
  type UpdateKnowledgeArticle,
  type UpdateKnowledgeFolder,
} from '@sbc/domain-models/knowledge-base';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, apiDelete, apiGet, apiSend } from './client';

const KB_KEY = ['knowledge-base'];
const TREE_KEY = [...KB_KEY, 'tree'];

function articleKey(id: string) {
  return [...KB_KEY, 'article', id] as const;
}

export function useKnowledgeTree() {
  return useQuery<KnowledgeTree>({
    queryKey: TREE_KEY,
    queryFn: () => apiGet('/api/knowledge-base/tree', KnowledgeTreeSchema),
  });
}

export function useKnowledgeArticle(id: string) {
  return useQuery<KnowledgeArticleDetail>({
    queryKey: articleKey(id),
    queryFn: () => apiGet(`/api/knowledge-base/articles/${id}`, KnowledgeArticleDetailSchema),
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKnowledgeFolder) =>
      apiSend('POST', '/api/knowledge-base/folders', input, KnowledgeFolderSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useUpdateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateKnowledgeFolder }) =>
      apiSend<KnowledgeFolder>(
        'PATCH',
        `/api/knowledge-base/folders/${id}`,
        input,
        KnowledgeFolderSchema,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/knowledge-base/folders/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKnowledgeArticle) =>
      apiSend('POST', '/api/knowledge-base/articles', input, KnowledgeArticleDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useUpdateArticle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateKnowledgeArticle) =>
      apiSend('PATCH', `/api/knowledge-base/articles/${id}`, input, KnowledgeArticleDetailSchema),
    onSuccess: (article) => {
      queryClient.setQueryData(articleKey(id), article);
      void queryClient.invalidateQueries({ queryKey: TREE_KEY });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/knowledge-base/articles/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

function useArticleAction(action: 'approve' | 'archive' | 'unarchive') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend(
        'POST',
        `/api/knowledge-base/articles/${id}/${action}`,
        {},
        KnowledgeArticleDetailSchema,
      ),
    onSuccess: (article) => {
      queryClient.setQueryData(articleKey(article.id), article);
      void queryClient.invalidateQueries({ queryKey: TREE_KEY });
    },
  });
}

export function useApproveArticle() {
  return useArticleAction('approve');
}

export interface BulkApproveResult {
  approved: string[];
  failed: { id: string; message: string }[];
}

// Bulk approve is a client-side fan-out over the per-article approve endpoint
// (no bulk endpoint on the server); each call is caught independently so one
// rejection never sinks the batch, and the caller gets a per-id summary.
export function useBulkApproveArticles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkApproveResult> => {
      const outcomes = await Promise.all(
        ids.map(async (id) => {
          try {
            await apiSend(
              'POST',
              `/api/knowledge-base/articles/${id}/approve`,
              {},
              KnowledgeArticleDetailSchema,
            );
            return { id, ok: true as const };
          } catch (caught) {
            return {
              id,
              ok: false as const,
              message: caught instanceof ApiError ? caught.message : 'Could not approve.',
            };
          }
        }),
      );
      return {
        approved: outcomes.filter((o) => o.ok).map((o) => o.id),
        failed: outcomes.filter((o) => !o.ok).map((o) => ({ id: o.id, message: o.message ?? '' })),
      };
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useArchiveArticle() {
  return useArticleAction('archive');
}

export function useUnarchiveArticle() {
  return useArticleAction('unarchive');
}

const PROPOSALS_KEY = [...KB_KEY, 'glossary-proposals'];

export function useGlossaryProposals(status: GlossaryProposalStatus | undefined) {
  return useQuery<GlossaryProposal[]>({
    queryKey: [...PROPOSALS_KEY, 'list', status ?? 'all'],
    queryFn: () =>
      apiGet(
        `/api/knowledge-base/glossary-proposals${status ? `?status=${status}` : ''}`,
        GlossaryProposalListSchema,
      ),
  });
}

export function useGlossaryProposal(id: string) {
  return useQuery<GlossaryProposal>({
    queryKey: [...PROPOSALS_KEY, id],
    queryFn: () => apiGet(`/api/knowledge-base/glossary-proposals/${id}`, GlossaryProposalSchema),
  });
}

export function useDecideGlossaryProposal(decision: 'approve' | 'reject') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend(
        'POST',
        `/api/knowledge-base/glossary-proposals/${id}/${decision}`,
        {},
        GlossaryProposalSchema,
      ),
    onSuccess: (proposal) => {
      queryClient.setQueryData([...PROPOSALS_KEY, proposal.id], proposal);
      void queryClient.invalidateQueries({ queryKey: PROPOSALS_KEY });
      // Approving edits and re-approves the glossary article.
      void queryClient.invalidateQueries({ queryKey: TREE_KEY });
    },
  });
}
